import React, {createContext, useEffect, useState, useCallback, useRef} from 'react';
import {useEventListener} from "./infrastructure/events/useBroadcastChannel";
import {ConnectionStatusEnum} from "./infrastructure/events/EventBridgeComponent";
import {fetchFileContent, listAllFiles} from "./domains/song/services/songs-db";
import {LineProcessor} from "./domains/song/services/line-processor";
import {JsonToSong} from "./domains/song/services/json-to-song";
import {
  getConnectionInfo,
  getDeviceInfo,
  updateConnectionInfo,
  updateDeviceInfo
} from "./infrastructure/storage/localStorage";
import {SessionManager} from "./infrastructure/storage/SessionManager";
import {ConnectionStatusEvent, SearchIndexRebuiltEvent, CLIENT_PROCESS_ID, FileBrowserFileSelectedChannel} from "./infrastructure/events/channels";
import yjsDocumentService from './infrastructure/sync/YjsDocumentService';
import {getWorkspaceId} from "./shared/services/dbUtils";
import {parseSessionShareHash} from "./shared/services/sessionShareUtils";
import {isWorkspaceReadOnly} from './shared/services/workspaceAccess';

import useZoomSettings from './shared/hooks/useZoomSettings';
import {presentationSelection} from './domains/song/services/presentation-selection';

const adoransSelectionChannel = new FileBrowserFileSelectedChannel();

export const AppCtx = createContext();

export const ViewTypes = Object.freeze({
  MONACO: 'monaco',
  SONGS: 'songs'
});

export const AppCtxProvider = ({children}) => {
  const [isCommonSessionEnabled, setIsCommonSessionEnabled] = useState(false);
  const [isFileBrowserVisible, setIsFileBrowserVisible] = useState(false);
  const [isTopMenuVisible, setIsTopMenuVisible] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const songEditorLocationRef = useRef(null);
  const [viewType, setViewType] = useState(ViewTypes.SONGS);
  const [selectedFile, setSelectedFile] = useState({name: null, found: false, content: null});
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const {zooms, setZoom} = useZoomSettings();
  const songViewFontSize = zooms.song;
  const setSongViewFontSize = useCallback(value => setZoom('song', value), [setZoom]);
  const [songEditFontSize, setSongEditFontSize] = useState(20);
  const [connectionStatus, setConnectionStatus] = useState(ConnectionStatusEnum.CHECKING_LOGIN);
  const [userInfo, setUserInfo] = useState({});
  const [deviceName, setDeviceName] = useState(getDeviceInfo().name);

  // Use SessionManager to get current session ID
  const currentSession = SessionManager.getCurrentSession();
  const [currentSessionId, setCurrentSessionId] = useState(currentSession?.id || null);

  const [connectedUsers, setConnectedUsers] = useState([]);
  const [areChordsVisible, setAreChordsVisible] = useState(true);
  const [arePresentationChordsVisible, setPresentationChordsVisible] = useState(true);
  const [isLyricSeparatorEnabled, setIsLyricSeparatorEnabled] = useState(false);
  const [isSongExpanded, setIsSongExpanded] = useState(false);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  // Map: fileName -> Set of collapsed song indices  
  const [collapsedSongs, setCollapsedSongs] = useState(new Map());
  const [isBlankScreen, setIsBlankScreen] = useState(false);
  const [isLyricsOnlyMode, setIsLyricsOnlyMode] = useState(false);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [sharedPresentationState, setSharedPresentationState] = useState(null);
  const [isPresentationControlPanelVisible, setPresentationControlPanelVisible] = useState(false);

  const [sharedGroupSelection, setSharedGroupSelection] = useState(null);

  const publishGroupSelection = useCallback((fileName, songNumber, line) => {
    try {
      const {doc} = yjsDocumentService.connectToDocument(getWorkspaceId());
      doc.getMap('fileBrowserSelections').set('latestSelection', {fileName, songNumber, line,
        __processId: CLIENT_PROCESS_ID, notifyOthers: true});
    } catch (error) {console.error('Error publishing group selection:', error);}
  }, []);

  // Function to refresh connected users from YJS awareness
  const refreshConnectedUsers = useCallback(() => {
    if (currentSessionId) {
      try {
        const workspaceId = getWorkspaceId();
        const users = yjsDocumentService.getAwarenessInfo(workspaceId);
        setConnectedUsers(users);

        // If connection is active and we have awareness data,
        // ensure the connection status is set to connected
        if (users.length > 0) {
          setConnectionStatus(ConnectionStatusEnum.WS_CONNECTED);
        }
      } catch (error) {
        console.error('Error refreshing connected users:', error);
      }
    }
  }, [currentSessionId]);

  useEffect(() => {
    // Update device info when device name changes
    updateDeviceInfo({...getDeviceInfo(), name: deviceName});
  }, [deviceName]);

  useEffect(() => {
    const sharedCredentials = parseSessionShareHash(window.location.hash);
    if (!sharedCredentials) return;

    try {
      const session = SessionManager.login(sharedCredentials.username, sharedCredentials.password);
      setCurrentSessionId(session.id);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (error) {
      console.error('Failed to apply shared session credentials:', error);
      alert('Nie udało się zastosować danych sesji z linku.');
    }
  }, []);

  useEffect(() => {
    // Update connection info when session ID changes
    updateConnectionInfo({...getConnectionInfo(), currentSessionId: currentSessionId});
  }, [currentSessionId]);

  useEffect(() => {
    // Setup YJS awareness subscription when common session is enabled
    let unsubscribeAwareness = null;

    if (currentSessionId) {
      try {
        const workspaceId = getWorkspaceId();
        unsubscribeAwareness = yjsDocumentService.subscribeToAwarenessEvents(
          workspaceId,
          (event) => {
            if (event && event.users) {
              setConnectedUsers(event.users);
              setConnectionStatus(ConnectionStatusEnum.WS_CONNECTED);
            }
          }
        );

        // Initial refresh of connected users
        refreshConnectedUsers();
      } catch (error) {
        console.error('Error setting up awareness subscription:', error);
        setConnectionStatus(ConnectionStatusEnum.ERROR);
      }
    }

    return () => {
      if (unsubscribeAwareness) {
        unsubscribeAwareness();
      }
    };
  }, [currentSessionId, refreshConnectedUsers]);

  useEffect(() => {
    setPresentationChordsVisible(areChordsVisible);
  }, [areChordsVisible]);

  useEffect(() => {
    if (process.env.REACT_APP_HOSANNA_DIRECT === '1' && !isCommonSessionEnabled) {
      setPresentationControlPanelVisible(false);
      setIsPresentationMode(false);
    }
  }, [isCommonSessionEnabled]);

  const publishPresentationState = useCallback((state) => {
    if (process.env.REACT_APP_HOSANNA_DIRECT === '1' && state) {
      try {
        const {text} = yjsDocumentService.connectToDocument(getWorkspaceId(), state.fileName);
        yjsDocumentService.updatePresentationState(getWorkspaceId(), presentationSelection(text.toString(), {
          ...state, __processId: CLIENT_PROCESS_ID
        }));
      } catch (error) { console.error('Error publishing presentation:', error); }
      return;
    }
    if (!state) {
      setSharedPresentationState(null);
      if ((isCommonSessionEnabled || process.env.REACT_APP_HOSANNA_DIRECT === '1') && currentSessionId && !isWorkspaceReadOnly(getWorkspaceId())) {
        try {
          yjsDocumentService.clearPresentationState(getWorkspaceId());
        } catch (error) {
          console.error('Error clearing shared presentation state:', error);
        }
      }
      return;
    }

    const payload = {
      ...state,
      updatedAt: new Date().toISOString(),
      __processId: CLIENT_PROCESS_ID
    };

    setSharedPresentationState(payload);

    if (isCommonSessionEnabled && isWorkspaceReadOnly(getWorkspaceId()) && state.fileName && state.songNumber) {
      adoransSelectionChannel.postMessage({
        fileName: state.fileName,
        songNumber: state.songNumber,
        notifyOthers: true
      });
    }

    if (isCommonSessionEnabled && currentSessionId && !isWorkspaceReadOnly(getWorkspaceId())) {
      try {
        yjsDocumentService.updatePresentationState(getWorkspaceId(), payload);
      } catch (error) {
        console.error('Error publishing shared presentation state:', error);
      }
    }
  }, [currentSessionId, isCommonSessionEnabled]);

  const clearPresentationState = useCallback(() => {
    setSharedPresentationState(null);

    if ((isCommonSessionEnabled || process.env.REACT_APP_HOSANNA_DIRECT === '1') && currentSessionId && !isWorkspaceReadOnly(getWorkspaceId())) {
      try {
        yjsDocumentService.clearPresentationState(getWorkspaceId());
      } catch (error) {
        console.error('Error clearing shared presentation state:', error);
      }
    }
  }, [currentSessionId, isCommonSessionEnabled]);

  useEffect(() => {
    if ((!isCommonSessionEnabled && process.env.REACT_APP_HOSANNA_DIRECT !== '1') || !currentSessionId) {
      if (!currentSessionId) { setSharedPresentationState(null); setSharedGroupSelection(null); }
      return undefined;
    }

    const workspaceId = getWorkspaceId();
    let isMounted = true;

    const unsubscribe = yjsDocumentService.subscribeToPresentationState(workspaceId, (state) => {
      if (!isMounted) return;
      setSharedPresentationState(state);
    });
    const unsubscribeGroup = yjsDocumentService.subscribeToGroupSelection(workspaceId, (selection) => {
      if (isMounted) setSharedGroupSelection(selection);
    });

    return () => {
      isMounted = false;
      unsubscribe?.();
      unsubscribeGroup?.();
    };
  }, [isCommonSessionEnabled, currentSessionId]);

  return (
    <AppCtx.Provider value={{
      isCommonSessionEnabled,
      setIsCommonSessionEnabled,
      viewType,
      setViewType,
      songEditorLocationRef,
      selectedFile,
      setSelectedFile,
      searchResults,
      setSearchResults,
      searchQuery,
      setSearchQuery,
      isFileBrowserVisible,
      setIsFileBrowserVisible,
      isTopMenuVisible,
      setIsTopMenuVisible,
      isFullScreen,
      setIsFullScreen,
      zooms,
      setZoom,
      songViewFontSize,
      setSongViewFontSize,
      songEditFontSize,
      setSongEditFontSize,
      connectionStatus,
      setConnectionStatus,
      userInfo,
      setUserInfo,
      deviceName,
      setDeviceName,
      currentSessionId,
      setCurrentSessionId,
      connectedUsers,
      setConnectedUsers,
      areChordsVisible,
      setAreChordsVisible,
      arePresentationChordsVisible,
      setPresentationChordsVisible,
      isLyricSeparatorEnabled,
      setIsLyricSeparatorEnabled,
      isSongExpanded,
      setIsSongExpanded,
      isPresentationMode,
      setIsPresentationMode,
      collapsedSongs,
      setCollapsedSongs,
      isBlankScreen,
      setIsBlankScreen,
      isLyricsOnlyMode,
      setIsLyricsOnlyMode,
      showQuickMenu,
      setShowQuickMenu,
      sharedPresentationState,
      publishPresentationState,
      publishGroupSelection,
      sharedGroupSelection,
      clearPresentationState,
      isPresentationControlPanelVisible,
      setPresentationControlPanelVisible,
      refreshConnectedUsers, // Add this function to allow manual refresh of connected users
    }}>
      {children}
    </AppCtx.Provider>
  );
};
